/**
 * 세션 토큰 파서
 * SESSION_TOKEN_SPEC.md 기반
 *
 * - `\` 이스케이프: `\` 다음 문자 1개는 리터럴로 출력 (토큰 파싱 제외)
 * - 토큰 패턴: `#{type::payload}` (type: planner, manager, timeslot)
 */

export type TokenPart =
  | { kind: 'text'; value: string }
  | { kind: 'planner'; payload: string }
  | { kind: 'manager'; payload: string }
  | { kind: 'timeslot'; payload: string }
  | { kind: 'timeslot-diff'; from: string; to: string }
  | { kind: 'address'; payload: string }
  | { kind: 'client'; payload: string };

const TOKEN_REGEX = /^#\{([a-z-]+)::([^}]+)\}/;

/**
 * 토큰 포함 문자열을 (text | token)[] 형태로 분해
 */
export function parseTokens(value: string): TokenPart[] {
  const result: TokenPart[] = [];
  let i = 0;
  let textBuffer = '';

  const flushText = () => {
    if (textBuffer) {
      result.push({ kind: 'text', value: textBuffer });
      textBuffer = '';
    }
  };

  while (i < value.length) {
    // 이스케이프: \ 다음 문자 1개는 리터럴
    if (value[i] === '\\') {
      flushText();
      i++;
      if (i < value.length) {
        result.push({ kind: 'text', value: value[i] });
        i++;
      }
      continue;
    }

    // 토큰 패턴 매칭
    const rest = value.slice(i);
    const match = rest.match(TOKEN_REGEX);
    if (match) {
      const [, type, payload] = match;
      if (type === 'planner') {
        flushText();
        result.push({ kind: 'planner', payload });
        i += match[0].length;
        continue;
      }
      if (type === 'manager') {
        flushText();
        result.push({ kind: 'manager', payload });
        i += match[0].length;
        continue;
      }
      if (type === 'timeslot') {
        flushText();
        result.push({ kind: 'timeslot', payload });
        i += match[0].length;
        continue;
      }
      if (type === 'timeslot-diff') {
        flushText();
        const [from, to] = payload.split('::');
        if (from !== undefined && to !== undefined) {
          result.push({ kind: 'timeslot-diff', from, to });
        } else {
          textBuffer += match[0];
        }
        i += match[0].length;
        continue;
      }
      if (type === 'address') {
        flushText();
        result.push({ kind: 'address', payload });
        i += match[0].length;
        continue;
      }
      if (type === 'client') {
        flushText();
        result.push({ kind: 'client', payload });
        i += match[0].length;
        continue;
      }
      // 알 수 없는 타입 → 텍스트로 처리
      textBuffer += match[0];
      i += match[0].length;
      continue;
    }

    textBuffer += value[i];
    i++;
  }

  flushText();
  return result;
}

/**
 * 문자열에 토큰이 포함되어 있는지 확인
 */
export function hasTokens(value: string): boolean {
  return /#\{[a-z-]+::[^}]+\}/.test(value);
}

export interface ExtractedPlannerOperatorIds {
  plannerIds: string[];
  operatorIds: string[];
}

/**
 * 문자열에서 planner/manager 토큰의 payload(ID) 추출
 */
export function extractPlannerOperatorIds(value: string): ExtractedPlannerOperatorIds {
  const parts = parseTokens(value);
  const plannerIds = new Set<string>();
  const operatorIds = new Set<string>();
  for (const part of parts) {
    if (part.kind === 'planner') plannerIds.add(part.payload);
    if (part.kind === 'manager') operatorIds.add(part.payload);
  }
  return {
    plannerIds: Array.from(plannerIds),
    operatorIds: Array.from(operatorIds),
  };
}

/**
 * 여러 문자열에서 planner/manager ID 수집
 */
export function extractPlannerOperatorIdsFromStrings(
  values: readonly string[]
): ExtractedPlannerOperatorIds {
  const plannerIds = new Set<string>();
  const operatorIds = new Set<string>();
  for (const value of values) {
    const extracted = extractPlannerOperatorIds(value);
    extracted.plannerIds.forEach((id) => plannerIds.add(id));
    extracted.operatorIds.forEach((id) => operatorIds.add(id));
  }
  return {
    plannerIds: Array.from(plannerIds),
    operatorIds: Array.from(operatorIds),
  };
}

/**
 * FieldHistory 형태에서 planner/manager ID 추출
 * (items[].fieldValues, items[].metadata, currentFieldValues)
 */
export function extractPlannerOperatorIdsFromFieldHistory(fieldHistory: {
  items?: readonly { fieldValues?: Record<string, string>; metadata?: Record<string, string> }[];
  currentFieldValues?: Record<string, string>;
}): ExtractedPlannerOperatorIds {
  const strings: string[] = [];
  if (fieldHistory.currentFieldValues) {
    strings.push(...Object.values(fieldHistory.currentFieldValues));
  }
  for (const item of fieldHistory.items ?? []) {
    if (item.fieldValues) strings.push(...Object.values(item.fieldValues));
    if (item.metadata) strings.push(...Object.values(item.metadata));
  }
  return extractPlannerOperatorIdsFromStrings(strings);
}
